<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { vOnClickOutside } from '@vueuse/components';
import { useTrack } from 'dashboard/composables';
import NextButton from 'next/button/Button.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';

export default {
  components: {
    NextButton,
    NextInput,
  },
  directives: {
    onClickOutside: vOnClickOutside,
  },
  props: {
    filterType: {
      type: Number,
      default: 0,
    },
    customViewsQuery: {
      type: Object,
      default: () => {},
    },
    openLastSavedItem: {
      type: Function,
      default: () => {},
    },
  },
  emits: ['close'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      show: true,
      name: '',
      visibility: 'personal',
      selectedTeamIds: [],
    };
  },

  computed: {
    isButtonDisabled() {
      if (this.visibility === 'team' && !this.selectedTeamIds.length) {
        return true;
      }
      return this.v$.name.$invalid;
    },
    isAdministrator() {
      return this.$store.getters.getCurrentRole === 'administrator';
    },
    // Visibility applies to conversation folders; admins pick who sees them.
    showVisibilityOptions() {
      return this.isAdministrator && this.filterType === 0;
    },
    teamsList() {
      return this.$store.getters['teams/getTeams'];
    },
    visibilityOptions() {
      return [
        {
          key: 'personal',
          label: this.$t('FILTER.CUSTOM_VIEWS.ADD.VISIBILITY.PERSONAL'),
        },
        {
          key: 'team',
          label: this.$t('FILTER.CUSTOM_VIEWS.ADD.VISIBILITY.TEAM'),
        },
        {
          key: 'account',
          label: this.$t('FILTER.CUSTOM_VIEWS.ADD.VISIBILITY.ACCOUNT'),
        },
      ];
    },
  },

  validations: {
    name: {
      required,
      minLength: minLength(1),
    },
  },

  methods: {
    onClose() {
      this.$emit('close');
    },
    async saveCustomViews() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      try {
        const payload = {
          name: this.name,
          filter_type: this.filterType,
          query: this.customViewsQuery,
        };
        if (this.showVisibilityOptions && this.visibility !== 'personal') {
          payload.visibility = this.visibility;
          if (this.visibility === 'team') {
            payload.team_ids = this.selectedTeamIds;
          }
        }
        await this.$store.dispatch('customViews/create', payload);
        this.alertMessage =
          this.filterType === 0
            ? this.$t('FILTER.CUSTOM_VIEWS.ADD.API_FOLDERS.SUCCESS_MESSAGE')
            : this.$t('FILTER.CUSTOM_VIEWS.ADD.API_SEGMENTS.SUCCESS_MESSAGE');
        this.onClose();

        useTrack();
      } catch (error) {
        const errorMessage = error?.message;
        this.alertMessage =
          errorMessage || this.filterType === 0
            ? errorMessage
            : this.$t('FILTER.CUSTOM_VIEWS.ADD.API_SEGMENTS.ERROR_MESSAGE');
      } finally {
        useAlert(this.alertMessage);
      }
      this.openLastSavedItem();
    },
  },
};
</script>

<template>
  <div
    v-on-click-outside="[
      () => $emit('close'),
      { ignore: ['#saveFilterTeleportTarget'] },
    ]"
    class="z-40 max-w-3xl lg:w-[500px] overflow-visible w-full border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg rounded-xl p-6 grid gap-6"
  >
    <h3 class="text-base font-medium leading-6 text-n-slate-12">
      {{ $t('FILTER.CUSTOM_VIEWS.ADD.TITLE') }}
    </h3>
    <form class="w-full grid gap-6" @submit.prevent="saveCustomViews">
      <NextInput
        v-model="name"
        :placeholder="$t('FILTER.CUSTOM_VIEWS.ADD.PLACEHOLDER')"
        :message="v$.name.$error && $t('FILTER.CUSTOM_VIEWS.ADD.ERROR_MESSAGE')"
        :message-type="v$.name.$error && 'error'"
        @blur="v$.name.$touch"
      />
      <div v-if="showVisibilityOptions" class="grid gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('FILTER.CUSTOM_VIEWS.ADD.VISIBILITY.LABEL') }}
        </span>
        <label
          v-for="option in visibilityOptions"
          :key="option.key"
          class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer"
        >
          <input
            v-model="visibility"
            type="radio"
            :value="option.key"
            class="cursor-pointer"
          />
          {{ option.label }}
        </label>
        <div
          v-if="visibility === 'team'"
          class="grid gap-1.5 ps-6 pt-1 max-h-40 overflow-y-auto"
        >
          <span v-if="!teamsList.length" class="text-sm text-n-slate-11">
            {{ $t('FILTER.CUSTOM_VIEWS.ADD.VISIBILITY.NO_TEAMS') }}
          </span>
          <label
            v-for="teamOption in teamsList"
            :key="teamOption.id"
            class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer"
          >
            <input
              v-model="selectedTeamIds"
              type="checkbox"
              :value="teamOption.id"
              class="cursor-pointer"
            />
            {{ teamOption.name }}
          </label>
        </div>
      </div>
      <div class="flex flex-row justify-end w-full gap-2">
        <NextButton faded slate sm @click.prevent="onClose">
          {{ $t('FILTER.CUSTOM_VIEWS.ADD.CANCEL_BUTTON') }}
        </NextButton>
        <NextButton solid blue sm :disabled="isButtonDisabled">
          {{ $t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON') }}
        </NextButton>
      </div>
    </form>
  </div>
</template>
